import React, { useContext, useEffect, useState } from "react";
import QRCode from "qrcode.react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@material-ui/core";
import {
  CameraAlt,
  Link,
  MoreVert,
  Smartphone
} from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3)
  },
  dialogPaper: {
    borderRadius: 16,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
  },
  contentPaper: {
    padding: theme.spacing(4),
    borderRadius: 12,
    textAlign: "center",
    background: "#fff",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)"
  },
  title: {
    fontWeight: 700,
    color: "#2d3748",
    marginBottom: theme.spacing(2),
    fontSize: "1.5rem"
  },
  subtitle: {
    color: "#718096",
    marginBottom: theme.spacing(3),
    fontSize: "1rem"
  },
  qrContainer: {
    padding: theme.spacing(2),
    backgroundColor: "#fff",
    borderRadius: 12,
    margin: theme.spacing(2, 0),
    border: "1px solid #e2e8f0",
    display: "inline-block"
  },
  qrCode: {
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
  },
  instructions: {
    marginTop: theme.spacing(3),
    textAlign: "left"
  },
  instructionList: {
    backgroundColor: "#f7fafc",
    borderRadius: 8,
    padding: theme.spacing(2)
  },
  listItem: {
    padding: theme.spacing(1, 0)
  },
  listIcon: {
    minWidth: 40,
    color: theme.palette.primary.main
  },
  timer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 2),
    backgroundColor: "#edf2f7",
    borderRadius: 20,
    display: "inline-block",
    fontWeight: 600,
    color: "#4a5568"
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(4)
  },
  brand: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: "1.2rem",
    marginBottom: theme.spacing(1)
  },
  modeTabs: {
    marginBottom: theme.spacing(2),
    width: "100%"
  },
  phoneForm: {
    width: "100%",
    maxWidth: 360,
    display: "grid",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  helperBox: {
    width: "100%",
    maxWidth: 420,
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    marginTop: theme.spacing(2),
    textAlign: "left"
  },
  pairingCode: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: "#ecfeff",
    border: "1px solid #a5f3fc",
    minWidth: 220
  },
  pairingCodeText: {
    fontSize: "2rem",
    fontWeight: 700,
    letterSpacing: "0.28rem",
    color: "#155e75"
  },
  errorText: {
    color: "#b91c1c"
  }
}));

const QR_EXPIRATION_SECONDS = 120;

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [qrCode, setQrCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(QR_EXPIRATION_SECONDS);
  const [mode, setMode] = useState("qr");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [loadingPairingCode, setLoadingPairingCode] = useState(false);
  const [pairingError, setPairingError] = useState("");
  const [refreshingQr, setRefreshingQr] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId || !open) return;

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode || "");
        setTimeLeft(data.qrcode ? QR_EXPIRATION_SECONDS : 0);
      } catch (err) {
        toastError(err);
      }
    };

    fetchSession();
  }, [open, whatsAppId]);

  useEffect(() => {
    if (!open || !qrCode) return undefined;

    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, qrCode]);

  useEffect(() => {
    if (!open || !whatsAppId) return undefined;

    const companyId = user.companyId;

    const onWhatsappData = data => {
      if (data.action !== "update" || data.session.id !== whatsAppId) {
        return;
      }

      setQrCode(data.session.qrcode || "");
      setTimeLeft(data.session.qrcode ? QR_EXPIRATION_SECONDS : 0);
      setRefreshingQr(false);

      if (data.session.status === "CONNECTED") {
        setPairingCode("");
        setPairingError("");
        onClose();
      }
    };

    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [open, onClose, socket, user.companyId, whatsAppId]);

  useEffect(() => {
    if (!open) {
      setMode("qr");
      setPhoneNumber("");
      setPairingCode("");
      setPairingError("");
      setLoadingPairingCode(false);
      setRefreshingQr(false);
      setTimeLeft(QR_EXPIRATION_SECONDS);
    }
  }, [open]);

  useEffect(() => {
    if (
      !open ||
      !whatsAppId ||
      mode !== "qr" ||
      !qrCode ||
      timeLeft !== 0 ||
      refreshingQr
    ) {
      return undefined;
    }

    let active = true;

    const refreshQr = async () => {
      try {
        setRefreshingQr(true);
        await api.put(`/whatsappsession/${whatsAppId}`);
      } catch (err) {
        if (active) {
          setRefreshingQr(false);
          toastError(err);
        }
      }
    };

    refreshQr();

    return () => {
      active = false;
    };
  }, [mode, open, qrCode, refreshingQr, timeLeft, whatsAppId]);

  const handleRequestPairingCode = async () => {
    if (!whatsAppId) return;

    const cleanedNumber = phoneNumber.replace(/\D/g, "");
    if (!cleanedNumber) {
      setPairingError("Informe o numero com DDI, DDD e telefone.");
      return;
    }

    setLoadingPairingCode(true);
    setPairingError("");

    try {
      const { data } = await api.post(
        `/whatsappsession/${whatsAppId}/pairing-code`,
        { phoneNumber: cleanedNumber }
      );

      setPairingCode(data.pairingCode || "");
      setQrCode("");
      setTimeLeft(0);
      setRefreshingQr(false);
    } catch (err) {
      setPairingCode("");
      setPairingError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Nao foi possivel gerar o codigo de pareamento."
      );
    } finally {
      setLoadingPairingCode(false);
    }
  };

  const instructions = [
    {
      icon: <Smartphone />,
      text: "Abra o WhatsApp no seu celular"
    },
    {
      icon: <MoreVert />,
      text: "Toque em Mais opcoes no Android ou em Configuracoes no iPhone"
    },
    {
      icon: <Link />,
      text: "Entre em Dispositivos conectados e toque em Conectar dispositivos"
    },
    {
      icon: <CameraAlt />,
      text: "Aponte a camera do celular para esta tela para ler o QR Code"
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogContent style={{ padding: 0 }}>
        <Paper className={classes.contentPaper}>
          <div className={classes.root}>
            <Typography className={classes.brand} />
            <Typography className={classes.title}>Conectar WhatsApp</Typography>
            <Typography className={classes.subtitle}>
              Use QR Code ou peca um codigo por numero quando a leitura falhar
            </Typography>

            <Tabs
              value={mode}
              onChange={(_, value) => setMode(value)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              className={classes.modeTabs}
            >
              <Tab value="qr" label="QR Code" />
              <Tab value="pairing" label="Conectar por numero" />
            </Tabs>

            {mode === "qr" ? (
              qrCode ? (
                <>
                  <div className={classes.qrContainer}>
                    <QRCode
                      value={qrCode}
                      size={280}
                      className={classes.qrCode}
                      fgColor="#2d3748"
                      bgColor="#ffffff"
                      level="H"
                    />
                  </div>

                  <div className={classes.timer}>
                    {timeLeft > 0
                      ? `QR expira em: ${timeLeft}s`
                      : refreshingQr
                        ? "Renovando QR automaticamente..."
                        : "Aguardando renovacao do QR pelo servidor..."}
                  </div>

                  <div className={classes.instructions}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Como conectar:
                    </Typography>
                    <List className={classes.instructionList} dense>
                      {instructions.map((instruction, index) => (
                        <ListItem key={index} className={classes.listItem}>
                          <ListItemIcon className={classes.listIcon}>
                            {instruction.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={instruction.text}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </div>

                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginTop: 16 }}
                  >
                    Se o QR continuar invalido, tente o fluxo "Conectar por numero"
                  </Typography>
                </>
              ) : (
                <div className={classes.loadingContainer}>
                  <CircularProgress size={40} style={{ color: "#667eea" }} />
                  <Typography variant="body1" color="textSecondary">
                    Aguardando pelo QR Code...
                  </Typography>
                </div>
              )
            ) : (
              <>
                <div className={classes.phoneForm}>
                  <TextField
                    label="Numero do celular"
                    variant="outlined"
                    fullWidth
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value)}
                    placeholder="5511999999999"
                    helperText="Informe somente numeros, com DDI. Ex.: 5511999999999"
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRequestPairingCode}
                    disabled={loadingPairingCode}
                  >
                    {loadingPairingCode ? "Gerando codigo..." : "Gerar codigo"}
                  </Button>
                </div>

                {pairingError ? (
                  <Box mt={2}>
                    <Typography variant="body2" className={classes.errorText}>
                      {pairingError}
                    </Typography>
                  </Box>
                ) : null}

                {pairingCode ? (
                  <div className={classes.pairingCode}>
                    <Typography variant="caption" color="textSecondary">
                      Codigo de pareamento
                    </Typography>
                    <Typography className={classes.pairingCodeText}>
                      {pairingCode}
                    </Typography>
                  </div>
                ) : null}

                <div className={classes.helperBox}>
                  <Typography variant="subtitle2" gutterBottom>
                    Como usar
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    No celular, abra WhatsApp, va em Dispositivos conectados e
                    escolha a opcao de conectar com codigo.
                  </Typography>
                </div>
              </>
            )}
          </div>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
